import { getBreakPoint, useScreenWidth } from "oolib";
import React, { useCallback, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

import { StyledStickyScrollbar, StyledTargetWrapper } from "./styled";

export const StickyHorizontalScrollbar = React.forwardRef(
  ({ style, children }, forwardedRef) => {
    const scrollbarRef = useRef(null);

    /**
     * Naive two-way scrollLeft sync creates a feedback loop: the table's
     * scroll sets the fake scrollbar's scrollLeft, whose own scroll event
     * then writes a (by now stale) value back onto the table. During a
     * trackpad momentum scroll that stale write fights the easing — visible
     * as a horizontal jitter right as the scroll settles. So: whichever
     * element fires first "owns" the scroll until events go quiet for a
     * beat, and scroll events coming from the other element (the echoes of
     * our own writes) are ignored.
     */
    const scrollOwnerRef = useRef(null);
    const releaseScrollOwnerTimeoutRef = useRef(null);

    useEffect(() => {
      return () => clearTimeout(releaseScrollOwnerTimeoutRef.current);
    }, []);

    const { ref: inViewRef, inView: targetInView, entry } = useInView();
    const targetElm = entry?.target;

    const { ref: bottomRef, inView: reachedBottom } = useInView();

    const screenWidth = useScreenWidth();

    // Combine the inView ref with the forwarded ref
    const targetRef = useCallback(
      (node) => {
        inViewRef(node);
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [inViewRef, forwardedRef]
    );

    const syncHorizontalScroll = (ev) => {
      if (!scrollbarRef.current) return;
      if (!targetElm) return;

      // an echo of our own scrollLeft write below — not the user
      if (scrollOwnerRef.current && scrollOwnerRef.current !== ev.target)
        return;

      scrollOwnerRef.current = ev.target;
      clearTimeout(releaseScrollOwnerTimeoutRef.current);
      releaseScrollOwnerTimeoutRef.current = setTimeout(() => {
        scrollOwnerRef.current = null;
      }, 150);

      const other =
        ev.target === targetElm ? scrollbarRef.current : targetElm;
      if (other.scrollLeft !== ev.target.scrollLeft) {
        other.scrollLeft = ev.target.scrollLeft;
      }
    };

    return (
      <>
        <StyledTargetWrapper ref={targetRef} onScroll={syncHorizontalScroll}>
          {children}
        </StyledTargetWrapper>
        {screenWidth > getBreakPoint("md") && (
          <>
            <div
              ref={bottomRef}
              data-sticky-scroll-sentinel
              style={{ height: 1, background: "transparent" }}
            />
            {targetElm && !reachedBottom && targetInView && (
              <StyledStickyScrollbar
                style={{ ...style, width: targetElm.clientWidth }}
                ref={scrollbarRef}
                onScroll={syncHorizontalScroll}
              >
                <div
                  style={{
                    height: "1.5rem",
                    minWidth: targetElm.scrollWidth,
                  }}
                ></div>
              </StyledStickyScrollbar>
            )}
          </>
        )}
      </>
    );
  }
);
